import { FOLDER_ICON, FolderItem, FolderWorkflowData } from './listView.utils';
import { defaultWorkflowCover } from './defaultCover';
import type { ArchiveItem } from '../Archive/archive.types';
import type { DashboardRecipe } from '@/types/recipes';

interface HasIdAndParent {
  id: string;
  parentId?: string | null;
}

export function countFilesInFolder(folderId: string, folders: HasIdAndParent[], files: HasIdAndParent[]): number {
  let count = files.filter((f) => f.parentId === folderId).length;

  const subfolders = folders.filter((f) => f.parentId === folderId);
  for (const subfolder of subfolders) {
    count += countFilesInFolder(subfolder.id, folders, files);
  }

  return count;
}

export function calculateArchiveFolderData(
  folders: ArchiveItem[],
  workflows: ArchiveItem[],
  isFplMigrationEnabled?: boolean,
): FolderWorkflowData {
  const data: FolderWorkflowData = {};

  for (const folder of folders) {
    const childFolders = folders.filter((f) => f.parentId === folder.id);
    const childWorkflows = workflows.filter((w) => w.parentId === folder.id);
    const items: FolderItem[] = [];
    childFolders.forEach(() => items.push(FOLDER_ICON));
    childWorkflows.forEach((w) => items.push(w.poster || defaultWorkflowCover(isFplMigrationEnabled)));
    data[folder.id] = {
      posters: items.slice(0, 4),
      count: childFolders.length + childWorkflows.length,
    };
  }

  return data;
}

/** The row's card shows its tool's thumbnail when the tool has one, and the canvas poster otherwise. */
const withToolCover = (workflow: DashboardRecipe): DashboardRecipe =>
  workflow.toolThumbnail ? { ...workflow, poster: workflow.toolThumbnail } : workflow;

/**
 * A Tool row, titled with the tool's own name. Only a Tool row: elsewhere the card is the workflow,
 * and Rename edits the workflow's name. It goes on `displayName`, so `name` stays the workflow's.
 */
const asToolRow = (workflow: DashboardRecipe): DashboardRecipe => ({
  ...withToolCover(workflow),
  ...(workflow.toolName && { displayName: workflow.toolName }),
  workflowKind: 'tool',
});

/** The name a dashboard row shows: its tool's, when {@link tagWorkflowKinds} gave it one. */
export const dashboardDisplayName = (item: { name: string; displayName?: string }): string =>
  item.displayName || item.name;

/**
 * Tags each row with its kind, so one mechanism drives the filter and the label. "Shared with me" has
 * already tagged its rows by grant and keeps that; elsewhere a row is a tool once it has one.
 *
 * The pointer is trustworthy here because the listing resolved it under the caller's own entitlement —
 * every tool they may open, a peer's included — so its absence means "no tool you can open" rather
 * than "not asked", and that reads the same as a plain workflow. Whichever label a row gets, it names
 * where the click lands: the server redirects a viewer entitled to the tool from /flow to /tool
 * (WEA-5088), and this labels exactly that row "Tool".
 *
 * The owner's workflow with a tool also gets a workflow row beside it, so the canvas stays one click
 * away. Only the owner's: /flow keeps the owner on the canvas but redirects anyone else to the tool.
 */
export function tagWorkflowKinds(
  workflows: DashboardRecipe[],
  areTabsEnabled: boolean,
  currentUserId?: string,
): DashboardRecipe[] {
  if (!areTabsEnabled) {
    // No row is told apart as a tool here, so any row whose tool has a thumbnail shows it.
    return workflows.map(withToolCover);
  }
  // A grant tag from the shared list wins: one workflow can reach the caller as both kinds, and only
  // the grant says which row is which.
  return workflows.flatMap((workflow): DashboardRecipe[] => {
    if (workflow.workflowKind) {
      return [workflow];
    }
    if (!workflow.toolId) {
      return [{ ...workflow, workflowKind: 'workflow' }];
    }
    const toolRow = asToolRow(workflow);
    if (!currentUserId || workflow.owner !== currentUserId) {
      return [toolRow];
    }
    // Without `toolId` the row opens the canvas; its own `rowId` keeps it apart from the tool row.
    const { toolId: _toolId, ...workflowOnly } = workflow;
    return [{ ...workflowOnly, workflowKind: 'workflow', rowId: `workflow:${workflow.id}` }, toolRow];
  });
}

/**
 * Stable identity for a row: its own `id` unless its list had to qualify it. Only the unified "Shared
 * with me" list does, where a both-ways share yields two rows under one `id` that would otherwise
 * collide in React keys, test ids, and the context menu's lookup. Elsewhere the bare `id` survives.
 */
export function dashboardRowId(row: Pick<DashboardRecipe, 'id' | 'rowId'>): string {
  return row.rowId ?? row.id;
}
